/**
 * The ONE receipt vocabulary (DESIGN.md §4.1/§6/§9).
 *
 * A receipt is the visible artifact of "the system saved something, and
 * it is undoable" (§2). This module is the view-model + adapter layer:
 * every surface that produces a finished action — the Eric dispatcher,
 * the fast-lane cascade endpoints, the shop-state quick actions — maps
 * its response into a `ReceiptVM`, and `<Receipt>` renders all of them
 * identically. Undo authority is the server (§6): the client's
 * `withinUndoWindow` mirror gates *visibility* only.
 *
 * Adapters take a `t` bound to the `receipts` namespace (the same
 * pass-a-t pattern as `mapUndoError`) so they stay pure and testable.
 */

import type { DispatchResponse } from '@/components/dashboard/agentic/types'
import type { ExecutableShopStateAction } from '@/lib/shop-state'
import { formatShortWhen } from '@/lib/dates'
import { withinUndoWindow } from '@/lib/threads'
import { track } from '@/lib/analytics'

// Receipts are 'the system saved it' artifacts (§2) — only the two
// system lanes exist here. Manual actions never produce receipts; the
// gray manual badge belongs to Activity feed rows (§2.1, PR-7).
export type ReceiptLane = 'assistant' | 'fast'

export type ReceiptVM = {
  /** Undo handle (`assistant_requests` row). Null = nothing to undo. */
  requestId: string | null
  /** §2.1 lane attribution — assistant, quick action, or manual. */
  lane: ReceiptLane
  /** Action kind for telemetry (§9 `action_kind`) — intent or cascade verb. */
  kind: string
  title: string
  sub: string | null
  /** Deep link for the Open action. Null hides Open. */
  url: string | null
  createdAtIso: string
  undoneAt: string | null
}

type TFunc = (key: string, opts?: Record<string, unknown>) => string

/**
 * §6 visibility rule: Undo shows while the server-mirrored window is
 * open AND the action hasn't been undone. When this flips false the
 * affordance disappears silently — no countdown, no expiry copy.
 */
export function undoableNow(vm: ReceiptVM): boolean {
  return vm.requestId !== null && vm.undoneAt === null && withinUndoWindow(vm.createdAtIso)
}

/**
 * Eric dispatcher turn → receipt. Only `mode: 'executed'` saved
 * something (§2 — green never decorates); preview/reply turns return
 * null and render as chat, not receipts.
 */
export function fromDispatchResponse(
  res: DispatchResponse,
  nowIso: string,
): ReceiptVM | null {
  if (res.mode !== 'executed') return null
  return {
    requestId: res.request_id ?? null,
    lane: 'assistant',
    kind: res.intent,
    title: res.summary,
    sub: res.result?.label ?? null,
    url: res.result?.url ?? null,
    createdAtIso: nowIso,
    undoneAt: null,
  }
}

/**
 * Fast-lane cascade result (book / reschedule / note / vehicle — the
 * `/api/dashboard/*` deterministic endpoints). The response shape is the
 * `BookForThreadResult` family: `{ ok, customer, starts_at_iso, url,
 * request_id }`.
 */
export function fromCascadeResult(
  verb: 'book' | 'reschedule' | 'add_note' | 'add_vehicle' | 'add_customer' | 'update',
  res: {
    ok: boolean
    customer?: { id: string; name: string } | null
    starts_at_iso?: string | null
    url?: string | null
    request_id?: string | null
    label?: string | null
  },
  t: TFunc,
  nowIso: string,
): ReceiptVM | null {
  if (!res.ok) return null
  const name = res.customer?.name ?? null
  return {
    requestId: res.request_id ?? null,
    lane: 'fast',
    kind: verb,
    title: t(`title.${verb}`, { name: name ?? t('title.fallback_name') }),
    sub: res.label ?? (res.starts_at_iso ? formatShortWhen(res.starts_at_iso) : null),
    url: res.url ?? null,
    createdAtIso: nowIso,
    undoneAt: null,
  }
}

/**
 * Shop-state quick action done (NextBestActions execute path —
 * `{ ok, request_id }` from the pre-bound PATCH descriptor).
 */
export function fromShopStateDone(
  action: ExecutableShopStateAction,
  requestId: string | null,
  t: TFunc,
  nowIso: string,
): ReceiptVM {
  return {
    requestId,
    lane: 'fast',
    kind: action.kind,
    title: t(`title.${action.kind}`, {
      name: action.target.customerName ?? t('title.fallback_name'),
    }),
    sub: action.target.vehicleLabel,
    url: `/appointments?appt=${encodeURIComponent(action.target.appointmentId)}`,
    createdAtIso: nowIso,
    undoneAt: null,
  }
}

// ── §9 telemetry contract ────────────────────────────────────────────
// PII-free by construction: IDs and enums only. These helpers are the
// only sanctioned emit points for the §9 events so property names never
// drift between surfaces.

export type ActionTier = 'prehighlighted' | 'chip' | 'typed'

export function trackTierTap(args: {
  verb: string
  tier: ActionTier
  surface: string
}): void {
  track('action.tier_tap', args)
}

export function trackUndoShown(vm: ReceiptVM, surface: string): void {
  if (!vm.requestId) return
  track('undo.shown', {
    request_id: vm.requestId,
    action_kind: vm.kind,
    surface,
  })
}

export function trackUndoTapped(vm: ReceiptVM): void {
  if (!vm.requestId) return
  track('undo.tapped', {
    request_id: vm.requestId,
    action_kind: vm.kind,
    age_ms: Date.now() - new Date(vm.createdAtIso).getTime(),
  })
}

export function trackUndoResult(vm: ReceiptVM, ok: boolean, errorCode?: string): void {
  if (!vm.requestId) return
  track('undo.result', {
    request_id: vm.requestId,
    ok,
    ...(errorCode ? { error_code: errorCode } : {}),
  })
}

export function trackNeedsYouConfirm(kind: string, requestId: string | null): void {
  track('needs_you.confirm', { kind, ...(requestId ? { request_id: requestId } : {}) })
}

export function trackNeedsYouRefuse(kind: string, requestId: string | null): void {
  track('needs_you.refuse', { kind, ...(requestId ? { request_id: requestId } : {}) })
}

export function trackVerbChipTap(verb: string, via: 'tap' | 'slash'): void {
  track('verb_chip_tap', { verb, via })
}
